import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, Eye, EyeOff, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function AdminCategories() {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {id?, name}
  const [name, setName] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/categories");
      setCats(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing({}); setName(""); };
  const openEdit = (c) => { setEditing(c); setName(c.name); };

  const save = async () => {
    if (!name.trim()) return toast.error("Le nom est obligatoire");
    try {
      if (editing.id) {
        await api.put(`/admin/categories/${editing.id}`, { name });
        toast.success("Catégorie modifiée");
      } else {
        await api.post("/admin/categories", { name, active: true });
        toast.success("Catégorie créée");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const toggleActive = async (c) => {
    await api.put(`/admin/categories/${c.id}`, { active: !c.active });
    load();
  };

  const remove = async (c) => {
    if (!window.confirm(`Supprimer la catégorie "${c.name}" ? Les questions ne seront pas supprimées.`)) return;
    await api.delete(`/admin/categories/${c.id}`);
    toast.success("Catégorie supprimée");
    load();
  };

  const move = async (index, dir) => {
    const arr = [...cats];
    const target = index + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[index], arr[target]] = [arr[target], arr[index]];
    setCats(arr);
    await api.post("/admin/categories/reorder", arr.map((c) => c.id));
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-extrabold uppercase tracking-tight">Catégories</h1>
          <p className="text-sm text-slate-500">L'ordre défini ici est respecté dans l'application joueur.</p>
        </div>
        <button data-testid="add-category-btn" onClick={openNew} className="flex items-center gap-2 rounded-xl bg-dh-red px-4 py-2.5 text-sm font-bold text-white active:scale-95">
          <Plus className="h-4 w-4" /> Nouvelle
        </button>
      </div>

      {loading ? (
        <Spinner />
      ) : (
        <div className="space-y-3" data-testid="categories-list">
          {cats.map((c, i) => (
            <div key={c.id} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4">
              <div className="flex flex-col">
                <button onClick={() => move(i, -1)} disabled={i === 0} className="text-slate-400 hover:text-dh-red disabled:opacity-20"><ArrowUp className="h-4 w-4" /></button>
                <button onClick={() => move(i, 1)} disabled={i === cats.length - 1} className="text-slate-400 hover:text-dh-red disabled:opacity-20"><ArrowDown className="h-4 w-4" /></button>
              </div>
              <div className="flex-1">
                <p className="font-display text-lg font-bold uppercase">{c.name}</p>
                <p className="text-xs text-slate-400">{c.question_count} question(s)</p>
              </div>
              <span className={`rounded-full px-3 py-1 text-xs font-bold ${c.active ? "bg-dh-green/10 text-dh-green" : "bg-slate-100 text-slate-400"}`}>
                {c.active ? "Active" : "Inactive"}
              </span>
              <button onClick={() => toggleActive(c)} title={c.active ? "Désactiver" : "Activer"} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100" data-testid={`toggle-category-${c.id}`}>
                {c.active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
              <button onClick={() => openEdit(c)} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"><Pencil className="h-4 w-4" /></button>
              <button onClick={() => remove(c)} className="rounded-lg p-2 text-dh-red hover:bg-dh-red/10"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          {cats.length === 0 && <p className="rounded-2xl border border-slate-200 bg-white p-6 text-center text-slate-400">Aucune catégorie.</p>}
        </div>
      )}

      {editing && (
        <Modal title={editing.id ? "Modifier la catégorie" : "Nouvelle catégorie"} onClose={() => setEditing(null)}>
          <input
            data-testid="category-name-input"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && save()}
            placeholder="Nom de la catégorie"
            className="w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-dh-red"
          />
          <div className="mt-4 flex justify-end gap-3">
            <button onClick={() => setEditing(null)} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold">Annuler</button>
            <button data-testid="save-category-btn" onClick={save} className="rounded-xl bg-dh-red px-4 py-2.5 text-sm font-bold text-white">Enregistrer</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

export function Spinner() {
  return <div className="flex h-40 items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-dh-red border-t-transparent" /></div>;
}

export function Modal({ title, children, onClose, wide = false }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className={`w-full ${wide ? "max-w-2xl" : "max-w-md"} rounded-2xl bg-white p-6`} onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-xl font-extrabold uppercase">{title}</h2>
          <button onClick={onClose} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
